import { forwardRef } from 'react';
import { ButtonShell } from '@round/ui-kit';
import { Tooltip, assets } from '@round/whitelabel-ui';
import cn from 'classnames';
import copy from 'copy-to-clipboard';
import { openInNewTab, showNotification } from 'helpers';
import styles from './PostUrlActions.module.css';

const { Copy: CopyIcon, ExternalLink: ExternalLinkIcon, Chart: ChartIcon } = assets;

type Props = {
    postUrl: string;
    onViewStats?: () => void;
    className?: string;
};

const PostUrlActions = forwardRef<HTMLDivElement, Props>(({ postUrl, onViewStats, className }, ref) => {
    const handleCopyPostUrl = () => {
        const isCopied = copy(postUrl);
        if (isCopied) {
            showNotification('Copied!', 'info');
        }
    };

    const handleViewPost = () => {
        openInNewTab(postUrl);
    };

    return (
        <div ref={ref} className={cn(styles.actions, className)}>
            <Tooltip.Root>
                {(anchor, setAnchorRef) => (
                    <>
                        <ButtonShell
                            ref={setAnchorRef}
                            onClick={handleCopyPostUrl}
                            aria-label="Copy post URL"
                            className={styles.button}
                        >
                            <CopyIcon />
                        </ButtonShell>
                        <Tooltip anchorElement={anchor}>
                            <Tooltip.Body>Copy post URL</Tooltip.Body>
                        </Tooltip>
                    </>
                )}
            </Tooltip.Root>

            <Tooltip.Root>
                {(anchor, setAnchorRef) => (
                    <>
                        <ButtonShell
                            ref={setAnchorRef}
                            onClick={handleViewPost}
                            aria-label="View post"
                            className={styles.button}
                        >
                            <ExternalLinkIcon className={styles.viewPostIcon} />
                        </ButtonShell>
                        <Tooltip anchorElement={anchor}>
                            <Tooltip.Body>View post</Tooltip.Body>
                        </Tooltip>
                    </>
                )}
            </Tooltip.Root>

            {onViewStats && (
                <Tooltip.Root>
                    {(anchor, setAnchorRef) => (
                        <>
                            <ButtonShell
                                ref={setAnchorRef}
                                onClick={onViewStats}
                                aria-label="View post stats"
                                className={styles.button}
                            >
                                <ChartIcon />
                            </ButtonShell>
                            <Tooltip anchorElement={anchor}>
                                <Tooltip.Body>View post stats</Tooltip.Body>
                            </Tooltip>
                        </>
                    )}
                </Tooltip.Root>
            )}
        </div>
    );
});

PostUrlActions.displayName = 'PostUrlActions';

export default PostUrlActions;
